// Phase 5 — Parts Assist payload contract.
// This is the ONLY shape the browser extension is allowed to push into OptiBay
// via window.postMessage. Anything else is rejected at the bridge boundary.
//
// CRITICAL CONTRACTS:
//   • Vendor-supplied price lands in `unit_cost` only. NEVER overrides
//     OptiBay's pricing engine — Phase 3.5 still derives the suggested sell.
//   • Captured payload is shown in a confirm modal — NEVER auto-saved.
//   • Extension does NOT write to the database; only the modal does, via
//     the canonical useCreatePartsLine() mutation.

export const PARTS_ASSIST_MESSAGE_TYPE = 'optibay:parts-assist:capture' as const;

export interface PartsAssistCapturePayload {
  /** Vendor identifier (e.g. "oreilly_firstcall", "napa_prolink", "generic",
   *  or "suggestion" when the capture originates from the Phase 6 suggestion
   *  layer rather than a vendor scrape). */
  source_vendor: string;
  /** Free-text vendor display label shown in the modal. */
  source_label?: string | null;
  /** Vendor part number / SKU. Optional — modal will warn if missing. */
  part_number?: string | null;
  /** Human-readable description (required by Phase 2 parts_lines schema). */
  description: string;
  /** Captured unit cost. Optional — modal will warn if missing. */
  unit_cost?: number | null;
  /** Quantity defaults to 1; modal blocks <= 0. */
  quantity?: number | null;
  /** Free-text availability snapshot (e.g. "In stock — 4 hubs"). */
  availability?: string | null;
  /** Optional core charge captured from vendor site. */
  core_charge?: number | null;
  /** Notes free-text from extension (NOT parsed as authority). */
  notes?: string | null;
  /** Vendor-side identifier (cart-line id, sku ref, etc.) — debug only. */
  external_id?: string | null;
  /** ISO timestamp at capture time. Defaults to now if missing. */
  captured_at?: string | null;
  /**
   * Phase 5C — confidence the capture is structured / trustworthy.
   * - 'high'    → adapter-driven, structured selectors hit
   * - 'partial' → fallback selectors / generic adapter / missing key fields
   * - 'manual'  → user-typed in the popup, no scrape
   * Optional & additive — older extensions omitting it are still valid.
   */
  capture_confidence?: 'high' | 'partial' | 'manual' | null;
}

export interface PartsAssistMessageEnvelope {
  type: typeof PARTS_ASSIST_MESSAGE_TYPE;
  version: 1;
  payload: PartsAssistCapturePayload;
}

export function isPartsAssistMessage(data: unknown): data is PartsAssistMessageEnvelope {
  if (!data || typeof data !== 'object') return false;
  const m = data as Record<string, unknown>;
  if (m.type !== PARTS_ASSIST_MESSAGE_TYPE) return false;
  if (m.version !== 1) return false;
  const p = m.payload as Record<string, unknown> | undefined;
  if (!p || typeof p !== 'object') return false;
  if (typeof p.description !== 'string' || !p.description.trim()) return false;
  if (typeof p.source_vendor !== 'string' || !p.source_vendor.trim()) return false;
  return true;
}
